import { z } from 'zod';
import { DateString, Uuid } from './common.js';
import { Fact } from './facts.js';
import { CORRECTION_SEMANTICS } from './vocab.js';
import type { CorrectionSemantic, FactType } from './vocab.js';

/**
 * `POST /facts/:lineageId/correct` — the one-gesture correction on any fact or
 * finding (server-design.md §3.3/§6.2). The office never learns which mechanism
 * fired; the options are the sentences a person would say.
 *
 * `end_date` is **required** for `no_longer_true` (it is the date the window
 * closes) and rejected on the others — `.strict()` members make that a
 * schema property, not a service check.
 */
export const CorrectFactBody = z.discriminatedUnion('semantic', [
  z
    .object({
      semantic: z.literal('wrong_person'),
      detail: z.string().optional(),
    })
    .strict(),
  z
    .object({
      semantic: z.literal('wrong_fact'),
      detail: z.string().optional(),
    })
    .strict(),
  z
    .object({
      semantic: z.literal('no_longer_true'),
      detail: z.string().optional(),
      /** Month precision is fine; the UI offers a month picker. */
      end_date: DateString,
    })
    .strict(),
  z
    .object({
      semantic: z.literal('do_not_surface'),
      detail: z.string().optional(),
    })
    .strict(),
  /**
   * `set` names only fields in {@link FIXABLE_FIELDS} for the fact's type, each
   * with its new value, or `null` to clear a detail the type does not require;
   * the server refuses anything else with `FIX_REFUSED`.
   */
  z
    .object({
      semantic: z.literal('fix'),
      detail: z.string().optional(),
      set: z.record(z.string(), z.string().trim().min(1).nullable()),
    })
    .strict(),
]);
export type CorrectFactBody = z.infer<typeof CorrectFactBody>;

/**
 * Drift guard: the vocabulary and this request union must stay the same four
 * semantics. Adding one to `CORRECTION_SEMANTICS` without adding its member here
 * (or the reverse) is a compile error, not a runtime surprise.
 */
CORRECTION_SEMANTICS satisfies readonly CorrectFactBody['semantic'][];

type SemanticsInSync = [Exclude<CorrectionSemantic, CorrectFactBody['semantic']>] extends [never]
  ? [Exclude<CorrectFactBody['semantic'], CorrectionSemantic>] extends [never]
    ? true
    : never
  : never;

export const CORRECTION_SEMANTICS_IN_SYNC: SemanticsInSync = true;

/**
 * The corrected fact as it now renders (removed things stay visible), plus the
 * logged correction decision.
 *
 * `auto_latched_decision_ids` carries the **auto-latch rule** (decided
 * 2026-08-26): a fact-level correction on a lineage with pending non-blocking
 * `claim_adjudication` rows marks them answered in the same transaction
 * (`consumed_by: 'server'`, recorded against this correction). One office
 * gesture never leaves a dangling row — so the queued row's popover can offer
 * every semantic.
 */
export const CorrectFactResponse = z.object({
  fact: Fact,
  decision_id: Uuid,
  auto_latched_decision_ids: z.array(Uuid),
});
export type CorrectFactResponse = z.infer<typeof CorrectFactResponse>;

/**
 * `POST /facts/:lineageId/sensitivity` is deliberately **not** typed here: the
 * office sensitivity toggle is out of stage 1 (Soroush, 2026-08-26) and the
 * endpoint stays dark. Its body gets written when the affordance ships
 * (ui-design.md §8 item 12).
 */

/**
 * **The fields "Fix it" offers, per fact type, in the office's words**
 * (2026-09-30). Plain text and dates only, and never a field that names a person
 * or organization, composes the fact's key, or drives its severity: those change
 * *which* fact this is or how alarming it reads, and are "Wrong" plus a note, a
 * rename, or an operator amendment instead. A type that is absent offers no fix.
 *
 * The server enforces this list, and a server test holds every entry to the
 * catalog, so it cannot name a field the catalog does not define.
 */
export type FixableFieldKind = 'text' | 'date';
export interface FixableField {
  readonly field: string;
  readonly label: string;
  readonly kind: FixableFieldKind;
}

const text = (field: string, label: string): FixableField => ({ field, label, kind: 'text' });
const date = (field: string, label: string): FixableField => ({ field, label, kind: 'date' });

export const FIXABLE_FIELDS: Partial<Record<FactType, readonly FixableField[]>> = {
  vitals: [date('born', 'Born'), text('birthplace', 'Birthplace')],
  education: [date('year', 'Year')],
  professional_license: [
    text('credential', 'Credential'),
    date('issued', 'Issued'),
    date('expires', 'Expires'),
  ],
  employment: [text('role', 'Role'), date('start', 'Started'), date('end', 'Ended')],
  board_seat: [date('since', 'Since'), date('until', 'Until')],
  association_membership: [date('since', 'Since'), date('until', 'Until')],
  financial_event: [date('date', 'Date'), text('magnitude', 'Amount')],
  investment: [text('amount', 'Amount'), date('date', 'Date'), text('detail', 'Detail')],
  litigation: [date('filed', 'Filed'), text('matter', 'What it is about')],
  interest: [text('detail', 'Detail')],
  event_participation: [text('detail', 'Detail')],
  achievement: [text('context', 'Context')],
  philanthropy: [text('detail', 'Detail'), date('since', 'Since')],
  social_tie: [date('since', 'Since')],
  org_relation: [text('detail', 'Detail')],
  org_profile: [
    text('description', 'Description'),
    text('location', 'Location'),
    text('jurisdiction', 'Where it is registered'),
    date('founded', 'Founded'),
    text('size', 'Size'),
    text('sector', 'Sector'),
    text('website', 'Website'),
  ],
  event_profile: [
    text('description', 'Description'),
    date('starts_on', 'Starts'),
    date('ends_on', 'Ends'),
    text('place', 'Place'),
    text('venue', 'Venue'),
    text('format', 'Format'),
    text('expected_size', 'Expected size'),
    text('website', 'Website'),
  ],
};

/** A real date as the record holds one: a year, a month, or a calendar day. */
export function isFixDate(value: string): boolean {
  if (/^(?!0000)\d{4}$/.test(value)) return true;
  if (/^(?!0000)\d{4}-(0[1-9]|1[0-2])$/.test(value)) return true;
  return DateString.safeParse(value).success;
}

/**
 * One changed detail in the office's words — "role now reads “Chair”", "ended
 * cleared". The decision log and the connector both say a fix with this.
 */
export function fixPhrase(label: string, value: string | null): string {
  return value === null
    ? `${label.toLowerCase()} cleared`
    : `${label.toLowerCase()} now reads “${value}”`;
}

/** The fixed details with their labels, in the order `set` gives them. */
export function fixedDetails(
  type: FactType,
  set: Readonly<Record<string, string | null>>,
): { label: string; value: string | null }[] {
  const offered = fixableFieldsFor(type);
  return Object.entries(set).map(([field, value]) => ({
    label: offered.find((spec) => spec.field === field)?.label ?? field,
    value,
  }));
}

export const fixableFieldsFor = (type: FactType): readonly FixableField[] =>
  FIXABLE_FIELDS[type] ?? [];
